/**
 * H8: chapter 2's starter chain on town-b (data/story/ch2.ts). Setup puts the keeper and a local fighter in town-b.
 * The chain is accepted and handed in on its own (no walk-backs): ch2-road counts on arrival, the keeper by talk, the
 * pocket by the World's own `kill` count (wolves of the zone), the duel by a won bout against the fighter (his seat is
 * the duelMatch rival: systems/duelMatch.ts). Idle outside the 256 plains world.
 */
import { CH2, CH2_FIGHTER, CH2_KEEPER, CH2_PLACE, CH2_ZONE } from '../data/story/ch2';
import { plains256 } from '../data/zones';
import { duelMatchOf } from './duelMatch';
import { advanceObjectives } from '../questProgress';
import type { Entity, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

export class Ch2StorySystem implements WorldSystem {
  readonly name = 'ch2Story';
  private keeper: Entity | null = null;
  private fighter: Entity | null = null;

  constructor(private readonly h: SystemHost) {}

  private get inTown(): boolean {
    const m = this.h.world.map;
    return plains256() && m.zone === CH2_ZONE && !m.interior;
  }

  private at(id: string, status?: string): boolean {
    const w = this.h.world;
    return w.currentQuest()?.id === id && (!status || w.progress.quest.status === status);
  }

  setup(): void {
    if (!this.inTown) return;
    const mk = (d: { name: string; title: string; sprite: string; at: readonly [number, number] }) =>
      this.h.add({ kind: 'npc', x: d.at[0], y: d.at[1], name: d.name, title: d.title, sprite: d.sprite, facing: 1 });
    this.keeper = mk(CH2_KEEPER);
    this.fighter = mk(CH2_FIGHTER);
  }

  talk(npc: Entity): boolean {
    if (!this.inTown || npc !== this.keeper) return false;
    const w = this.h.world;
    const q = w.currentQuest();
    this.bump('talk', 'keeper');
    this.h.emit({ type: 'dialog', npcId: npc.id, text: q?.id === CH2.keeper ? q.done : 'ยินดีต้อนรับสู่เหมืองเงาผา', action: null }); // TODO-polish
    return true;
  }

  observe(ev: WorldEvent): void {
    if (!this.inTown) return;
    const w = this.h.world;
    if (ev.type === 'pvpDuel' && ev.phase === 'result' && ev.winner === w.player.id && this.at(CH2.duel, 'active')) this.bump('duelWin');
  }

  step(): void {
    if (!this.inTown) {
      // On the plains the road quest is taken up at once, so the log shows it.
      if (plains256() && this.at(CH2.road, 'available')) this.h.world.apply({ t: 'questAccept' });
      return;
    }
    const w = this.h.world;
    const q = w.currentQuest();
    if (!q || !(q.id as string).startsWith('ch2-')) return;
    if (w.progress.quest.status === 'available') w.apply({ t: 'questAccept' });
    if (this.at(CH2.road, 'active')) this.bump('visit', CH2_PLACE);
    if (this.at(CH2.duel, 'active') && this.fighter && w.entities.has(this.fighter.id)) {
      const d = duelMatchOf(w);
      if (d && d.rivalId < 0) d.rivalId = this.fighter.id;
    }
    if (w.progress.quest.status === 'ready') w.apply({ t: 'questTurnIn' });
  }

  private bump(type: 'talk' | 'visit' | 'duelWin', target?: string): void {
    const w = this.h.world;
    const q = w.currentQuest();
    const qs = w.progress.quest;
    if (!q || qs.status !== 'active') return;
    const r = advanceObjectives(q, qs.progress, type, target, { loose: true });
    if (r === 'ready') {
      qs.status = 'ready';
      this.h.emit({ type: 'quest', status: 'ready', questId: q.id });
    } else if (r) this.h.emit({ type: 'quest', status: 'progress', questId: q.id });
  }
}
